import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { LeftIcon, RightIcon } from '@/components/ui/icon';
import { IconButton } from '@/components/common/icon-button';
import { TruncatedText } from '@/components/common/truncated-text';
import { AgentOutlineIcon, UserEditIcon } from '@/components/agent-dashboard/assets';
import { useUserManagementHooks } from '@/manager';
import { TextButton } from '@/components/common/text-button';
import { cn } from '@/lib/utils';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { ConfirmModal } from '@/components/common/confirm-modal';

interface UserDetailHeaderProps {
  username: string;
  updateLoading?: boolean;
  updateUser: () => Promise<boolean>;
  updatePushSettings?: () => Promise<boolean>;
  updateArticleSetting?: () => Promise<boolean>;
  validatePushSettings?: () => boolean;
  validateUsers: () => boolean;
  resetUserDetail: () => void;
  resetPushSettings?: () => void;
  scrollTo?: (key: string) => void;
}

/** 用户详情顶部 */
export const UserDetailHeader = ({
  username,
  updateLoading,
  updateUser,
  updatePushSettings,
  updateArticleSetting,
  validatePushSettings,
  validateUsers,
  resetUserDetail,
  resetPushSettings,
  scrollTo,
}: UserDetailHeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { isUserInfoChanged, isPushSettingsChanged, isArticleSettingChanged } = useUserManagementHooks.useUserDetailChanged();

  // 统一更改状态
  const hasUnsavedChange = useMemo(
    () => isUserInfoChanged || isPushSettingsChanged || isArticleSettingChanged,
    [isUserInfoChanged, isPushSettingsChanged, isArticleSettingChanged]
  );

  // 返回上一页
  const goBack = useCallback(() => {
    navigate(`/agent/dashboard/user-management/${params.id}`);
  }, [navigate]);

  // 离开前检查
  const checkBeforeLeave = useCallback((next?: () => void) => {
    if (!hasUnsavedChange) {
      next?.();
      return;
    }
    setShowConfirmModal(true);
  },
    [hasUnsavedChange]
  );

  // 保存
  const handleSave = async () => {
    try {
      // 先校验数据
      const validUser = validateUsers();
      const validPushSettings = validatePushSettings?.();

      if (!validUser || !validPushSettings) {
        if (!validUser) {
          scrollTo?.('basic-info');
          return;
        }
        if (!validPushSettings) {
          scrollTo?.('push');
        }
        return;
      };

      // 校验通过，执行保存操作
      const tasks: Promise<boolean>[] = [];

      if (isUserInfoChanged) tasks.push(updateUser());
      if (isPushSettingsChanged && updatePushSettings) tasks.push(updatePushSettings());
      if (isArticleSettingChanged) tasks.push(updateArticleSetting());

      const results = await Promise.all(tasks);

      if (results.every((result) => result)) {
        goBack();
      }

    } catch (error) {
      console.error('Save user detail failed:', error);
    }
  };

  // 取消
  const handleCancel = () => {
    checkBeforeLeave(goBack);
  };

  // 确认取消（确认弹窗的确认）
  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    resetUserDetail();
    resetPushSettings?.();
    goBack();
  };

  // 去智能体管理页
  const handleToAgent = () => {
    checkBeforeLeave(() => navigate('/agent/'));
  };

  // 页面关闭保护
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedChange) return;
      e.preventDefault();
      e.returnValue = 'd';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChange]);

  return (
    <header className='flex justify-between w-full max-h-20 items-center px-10 pt-[24px] pb-3.5 border-b border-[rgba(233,234,235,1)] overflow-hidden'>
      <div className='flex'>
        <div className='mr-7'>
          <IconButton
            icon={<LeftIcon />}
            className='size-10 border border-[rgba(208,213,221,1)] rounded-[8px] hover:bg-gray-50'
            onClick={handleCancel}
          />
        </div>

        <div className='flex items-center min-w-full space-x-2'>
          {/* <IconButton icon={<AgentOutlineIcon className='size-5' />} onClick={handleToAgent} />
          <RightIcon className='size-4 text-[rgba(152,162,179,1)]' /> */}
          <div
            className='flex items-center text-sm font-medium text-[rgba(102,112,133,1)] cursor-pointer hover:text-gray-600'
            onClick={handleCancel}
          >
            <UserEditIcon className='inline-block mr-1.5' />
            <span>{t('agent_dashboard.user_detail.header')}</span>
          </div>
          <RightIcon className='size-4 text-[rgba(152,162,179,1)]' />
          {username && (
            <TruncatedText
              text={username}
              className='max-w-80 text-sm py-1 px-2 font-semibold text-[rgba(16,24,40,1)] bg-[rgba(242,244,247,1)] rounded-sm'
            />
          )}
        </div>
      </div>

      <div className='relative flex justify-end space-x-3'>
        <TextButton
          label={t('agent_dashboard.confirm_modal.cancel')}
          className={cn(
            '!w-20 h-10 bg-white',
            'font-semibold text-sm !text-[rgba(65,70,81,1)]',
            'border border-[rgba(213,215,218,1)] hover:bg-gray-50'
          )}
          onClick={handleCancel}
        />
        <TextButton
          label={t('agent_dashboard.confirm_modal.save')}
          onClick={handleSave}
          disabled={updateLoading || !hasUnsavedChange}
          className={cn('!w-[4.063rem] h-10', 'font-semibold text-sm text-white', 'border-none')}
        />
      </div>

      <ConfirmModal
        open={showConfirmModal}
        title={t('agent_dashboard.confirm_modal.cancel_title')}
        description={t('agent_dashboard.confirm_modal.cancel_desc')}
        cancel={t('agent_dashboard.confirm_modal.cancel')}
        confirm={t('agent_dashboard.confirm_modal.confirm')}
        onConfirm={handleCancelConfirm}
        onOpenChange={setShowConfirmModal}
      />
    </header>
  );
};
